import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Phone, Mail, MapPin, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      // Submit to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      toast.success('Message sent! We will get back to you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    }
  };

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-black mb-4">
            <span className="text-white">GET IN </span>
            <span className="text-yellow-400">TOUCH</span>
          </h1>
          <p className="text-xl text-gray-300 font-medium">We're here to help! Reach out to us anytime.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="border-2 border-yellow-500 shadow-lg bg-gray-900">
              <CardContent className="p-8">
                <h2 className="text-3xl font-black mb-6 text-yellow-400">CONTACT INFO</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-yellow-500 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-white mb-1">Phone</h3>
                      <a href="tel:9601382424" className="text-yellow-400 font-bold text-xl hover:underline">
                        9601382424
                      </a>
                      <p className="text-gray-400 text-sm font-medium mt-1">Mon - Sun, 8:00 AM - 11:00 PM</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-yellow-500 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-white mb-1">WhatsApp</h3>
                      <a 
                        href="https://wa.me/919601382424" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-yellow-400 font-bold text-xl hover:underline"
                      >
                        Chat with us
                      </a>
                      <p className="text-gray-400 text-sm font-medium mt-1">Quick responses guaranteed</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-yellow-500 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-white mb-1">Email</h3>
                      <a href="mailto:desistuff2016@gmail.com" className="text-yellow-400 font-bold text-xl hover:underline">
                        desistuff2016@gmail.com
                      </a>
                      <p className="text-gray-400 text-sm font-medium mt-1">We'll respond within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-yellow-500 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-white mb-1">Locations</h3>
                      <p className="text-gray-300 font-medium">6 outlets across Ahmedabad</p>
                      <p className="text-gray-400 text-sm font-medium mt-1">Check our Locations page for details</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="border-2 border-yellow-500 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-black mb-4">QUICK ACTIONS</h3>
                <div className="space-y-3">
                  <a href="https://wa.me/919601382424" target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-black text-yellow-400 hover:bg-gray-900 font-bold text-lg py-6">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Order on WhatsApp
                    </Button>
                  </a>
                  <a href="tel:9601382424">
                    <Button variant="outline" className="w-full border-2 border-black text-black hover:bg-black hover:text-yellow-400 font-bold text-lg py-6">
                      <Phone className="w-5 h-5 mr-2" />
                      Call Now
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="border-2 border-yellow-500 shadow-xl bg-gray-900">
              <CardContent className="p-8">
                <h2 className="text-3xl font-black mb-6 text-yellow-400">SEND MESSAGE</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-base font-bold text-yellow-400">Your Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      className="mt-2 border-2 border-gray-700 bg-black text-white focus:border-yellow-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-base font-bold text-yellow-400">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className="mt-2 border-2 border-gray-700 bg-black text-white focus:border-yellow-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-base font-bold text-yellow-400">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your contact number (optional)"
                      className="mt-2 border-2 border-gray-700 bg-black text-white focus:border-yellow-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base font-bold text-yellow-400">Your Message *</Label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help you..."
                      rows="6"
                      className="w-full mt-2 px-3 py-2 border-2 border-gray-700 bg-black text-white rounded-md focus:border-yellow-500 focus:outline-none font-medium"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full text-lg font-bold bg-yellow-500 hover:bg-yellow-400 text-black py-6"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-2xl p-8 border-2 border-yellow-400 shadow-lg max-w-4xl mx-auto">
          <h3 className="text-3xl font-black mb-4 text-black">PREFER DIRECT CONTACT?</h3>
          <p className="text-lg text-black font-medium mb-6">
            For immediate assistance, call us or message on WhatsApp
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:9601382424">
              <Button size="lg" className="font-bold bg-black text-yellow-400 hover:bg-gray-900 px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call 9601382424
              </Button>
            </a>
            <a href="https://wa.me/919601382424" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="font-bold border-2 border-black text-black hover:bg-black hover:text-yellow-400 px-8 py-6">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
